import type { Automation, AutomationBranchRoute, MomentId, Stage } from '@capture/shared';
import { STEP_LABELS, effectByKey, stageForStep, stepsForStage } from '@capture/shared';
import { SectionCard } from './SectionCard';
import styles from './panel.module.css';

interface PipelineStepRow {
  stepType: string;
  label: string;
}

interface Props {
  automation: Automation;
  pipelineSteps: PipelineStepRow[];
  onChange: (next: Automation) => void;
}

/** F-024 — pipeline step words that map onto a rule-capable Stage (mirrors
 *  RuleAutomationDrawer's RULE_STAGE_STEP_TYPES). */
const EDIT_CAPABLE_STEP_TYPES = new Set(['key', 'verify', 'qa', 'qc', 'supervisor']);

/** Task 6 (E-062) — the route moment rows, registry-aligned. The two dead
 *  batch moments stay VISIBLE (E-059: disabled with the reason, never hidden)
 *  so a legacy automation authored there still displays; they are only
 *  selectable when they already ARE the automation's moment. */
const ROUTE_MOMENTS: { value: MomentId; label: string; stepChips?: boolean; anyStepGhost?: boolean; dead?: string }[] = [
  { value: 'step_enter', label: 'When a document enters a step', stepChips: true, anyStepGhost: true },
  { value: 'on_validate', label: 'When a document is accepted at a step', stepChips: true },
  { value: 'pre_export', label: 'At export' },
  {
    value: 'batch_classified', label: 'When the batch is classified',
    dead: 'No automation can run here — batch classification has no action runtime.',
  },
  {
    value: 'batch_complete', label: 'When the batch completes',
    dead: 'No automation can run here — use the batch gate (checks) instead.',
  },
  { value: 'scheduled', label: 'On a schedule' },
];

function routeActionKeys(automation: Automation): string[] {
  return automation.branches
    .filter((b): b is AutomationBranchRoute => b.family === 'route')
    .flatMap((b) => b.actions.map((a) => a.effectKey));
}

/** A moment is offerable when EVERY route action's effect declares it (the
 *  branch compiles to one rule — one moment must serve all its actions).
 *  Unknown/legacy effect keys impose no constraint. */
function momentDisabledReason(automation: Automation, moment: MomentId, dead?: string): string | undefined {
  if (automation.moment === moment) return undefined; // current moment always stays selectable
  if (dead) return dead;
  for (const key of routeActionKeys(automation)) {
    const effect = effectByKey(key);
    if (effect && !effect.validMoments.includes(moment)) {
      return `"${effect.label}" can't run at this moment — it runs ${effect.validMoments.join(', ')}.`;
    }
  }
  return undefined;
}

function stageLabelList(stages: Stage[], pipelineSteps: PipelineStepRow[]): string {
  const labels = pipelineSteps
    .filter((s) => EDIT_CAPABLE_STEP_TYPES.has(s.stepType) && stages.includes(stageForStep(s.stepType)))
    .map((s) => s.label || STEP_LABELS[s.stepType as keyof typeof STEP_LABELS] || s.stepType);
  if (labels.length > 0) return [...new Set(labels)].join(', ');
  return stages
    .map((stage) => stepsForStage(stage).map((s) => STEP_LABELS[s]).join('/'))
    .join(', ');
}

function whereWhenSummary(automation: Automation, pipelineSteps: PipelineStepRow[]): string {
  if (automation.branches[0]?.family === 'route') {
    const row = ROUTE_MOMENTS.find((m) => m.value === automation.moment);
    if (automation.moment === 'step_enter') {
      return automation.stages?.length
        ? `Entering ${stageLabelList(automation.stages, pipelineSteps)}`
        : 'Entering any step';
    }
    if (automation.moment === 'on_validate') {
      return automation.stages?.length
        ? `When accepted at ${stageLabelList(automation.stages, pipelineSteps)}`
        : 'When accepted at any step';
    }
    return row?.label ?? 'Not set';
  }
  const stages = automation.stages ?? [];
  if (stages.length === 0) return 'No steps selected';
  return `At ${stageLabelList(stages, pipelineSteps)}`;
}

/**
 * Where & when — for check-family automations, a checkbox per edit-capable
 * pipeline step (writes `stages[]` via `stageForStep`, deduped). For
 * route-family automations, the registry-aligned moment radio group: moments
 * the branch's effects can't run at are disabled WITH the reason (E-059), the
 * two step-identity moments (`step_enter`, `on_validate` — R1/R2) carry
 * inline step chips writing the same `stages[]`, and step_enter's explicit
 * ghost chip renders the honest "any step" for unscoped rules (F-054).
 */
export function WhereWhenSection({ automation, pipelineSteps, onChange }: Props) {
  const isRoute = automation.branches[0]?.family === 'route';
  const editableSteps = pipelineSteps.filter((s) => EDIT_CAPABLE_STEP_TYPES.has(s.stepType));
  const hasQaStep = editableSteps.some((s) => s.stepType === 'qa');
  const hasQcStep = editableSteps.some((s) => s.stepType === 'qc');
  const showQaQcHint = hasQaStep && hasQcStep;

  function toggleStep(stepType: string, checked: boolean) {
    const currentlyChecked = new Set(
      editableSteps
        .filter((s) => (automation.stages ?? []).includes(stageForStep(s.stepType)))
        .map((s) => s.stepType),
    );
    if (checked) currentlyChecked.add(stepType);
    else currentlyChecked.delete(stepType);
    const stages: Stage[] = [
      ...new Set(editableSteps.filter((s) => currentlyChecked.has(s.stepType)).map((s) => stageForStep(s.stepType))),
    ];
    onChange({ ...automation, stages });
  }

  function toggleStepChip(stepType: string) {
    const stage = stageForStep(stepType);
    const current = automation.stages ?? [];
    const stages = current.includes(stage) ? current.filter((s) => s !== stage) : [...current, stage];
    onChange({ ...automation, stages });
  }

  return (
    <div data-testid="wherewhen-section">
      <SectionCard title="Where & when" summary={whereWhenSummary(automation, pipelineSteps)} defaultOpen>
        <div className={styles.formGrid}>
          {isRoute ? (
            <>
              <span className={styles.fieldLabel} id="wherewhen-when-label">
                When
              </span>
              {/* Builder follow-ups (item f) */}
              <div className={styles.radioGroup} role="radiogroup" aria-labelledby="wherewhen-when-label">
                {ROUTE_MOMENTS.map((m) => {
                  const disabledReason = momentDisabledReason(automation, m.value, m.dead);
                  const selected = automation.moment === m.value;
                  return (
                    <div key={m.value}>
                      <label className={styles.checkboxLabel} title={disabledReason}>
                        <input
                          type="radio"
                          name="automation-moment"
                          checked={selected}
                          disabled={disabledReason !== undefined}
                          onChange={() =>
                            onChange({ ...automation, moment: m.value, stages: m.stepChips ? [] : undefined })
                          }
                        />
                        {m.label}
                      </label>
                      {selected && m.stepChips && (
                        <div className={styles.chipRow}>
                          {m.anyStepGhost && (
                            <button
                              type="button"
                              className={styles.chip}
                              data-testid="wherewhen-any-step-chip"
                              aria-pressed={(automation.stages ?? []).length === 0}
                              onClick={() => onChange({ ...automation, stages: [] })}
                            >
                              any step
                            </button>
                          )}
                          {editableSteps.map((s) => (
                            <button
                              key={s.stepType}
                              type="button"
                              className={styles.chip}
                              data-testid={`wherewhen-step-chip-${s.stepType}`}
                              aria-pressed={(automation.stages ?? []).includes(stageForStep(s.stepType))}
                              onClick={() => toggleStepChip(s.stepType)}
                            >
                              {s.label || STEP_LABELS[s.stepType as keyof typeof STEP_LABELS] || s.stepType}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <span className={styles.fieldLabel}>Steps</span>
              <div className={styles.checkboxGroup}>
                {editableSteps.map((s) => (
                  <div key={s.stepType}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={(automation.stages ?? []).includes(stageForStep(s.stepType))}
                        onChange={(e) => toggleStep(s.stepType, e.target.checked)}
                      />
                      {s.label || STEP_LABELS[s.stepType as keyof typeof STEP_LABELS] || s.stepType}
                    </label>
                    {showQaQcHint && (s.stepType === 'qa' || s.stepType === 'qc') && (
                      <span className={styles.stepHint}>(QA and QC share one stage)</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {!isRoute && (
            <>
              <span className={styles.fieldLabel} id="wherewhen-timing-label">
                Timing
              </span>
              {/* P6 T3 — the "Leaving the step" (on-step-exit) timing option was removed:
                  it was a permanently-disabled radio with a promised-runtime badge
                  and no runtime backing. The on-exit moment returns as a real choice only
                  once on-step-exit executor support exists (git history + the coverage matrix
                  carry the context). Rules fire on step ENTRY today. */}
              <div className={styles.radioGroup} role="radiogroup" aria-labelledby="wherewhen-timing-label">
                <label className={styles.checkboxLabel}>
                  <input type="radio" name="automation-timing" checked readOnly />
                  Entering the step
                </label>
              </div>
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
